import * as z from 'zod';

export const CreateProjectItemRequest = z.object({
  item: z
    .intersection(
      z.object({
        projectId: z
          .string()
          .describe('Project ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _id: z
          .string()
          .describe('Project item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sortOrder: z
          .number()
          .describe(
            'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
          )
          .optional()
          .nullable(),
        title: z.string().describe('Project item title.').optional().nullable(),
        description: z
          .string()
          .describe('Project item description.')
          .optional()
          .nullable(),
        type: z.enum(['UNDEFINED', 'IMAGE', 'VIDEO']).optional(),
        _createdDate: z
          .date()
          .describe('Date and time the project item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the project item was last updated.')
          .optional()
          .nullable(),
        link: z
          .object({
            text: z
              .string()
              .describe('Display text of the link.')
              .optional()
              .nullable(),
            url: z
              .string()
              .describe('Target URL of the link.')
              .url()
              .optional()
              .nullable(),
            target: z
              .string()
              .describe(
                "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
              )
              .max(50)
              .optional()
              .nullable(),
          })
          .describe('Project item link.')
          .optional(),
      }),
      z.xor([
        z.object({ image: z.never().optional(), video: z.never().optional() }),
        z.object({
          video: z.never().optional(),
          image: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
            })
            .describe('Information about the Wix Media image.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z
            .object({
              videoInfo: z
                .string()
                .describe('Information about the Wix Media video.')
                .optional(),
              durationInMillis: z
                .number()
                .int()
                .describe('Manually defined Video duration in milliseconds.')
                .optional()
                .nullable(),
            })
            .describe('Information about the Wix Media video.'),
        }),
      ])
    )
    .describe('Project item to create.'),
});
export const CreateProjectItemResponse = z.intersection(
  z.object({
    projectId: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    _id: z
      .string()
      .describe('Project item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    sortOrder: z
      .number()
      .describe(
        'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project item title.').optional().nullable(),
    description: z
      .string()
      .describe('Project item description.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
      .describe('Project item data type.')
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the project item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project item was last updated.')
      .optional()
      .nullable(),
    link: z
      .object({
        text: z
          .string()
          .describe('Display text of the link.')
          .optional()
          .nullable(),
        url: z
          .string()
          .describe('Target URL of the link.')
          .url()
          .optional()
          .nullable(),
        target: z
          .string()
          .describe(
            "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Project item link.')
      .optional(),
  }),
  z.xor([
    z.object({ image: z.never().optional(), video: z.never().optional() }),
    z.object({
      video: z.never().optional(),
      image: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Information about the Wix Media image.'),
    }),
    z.object({
      image: z.never().optional(),
      video: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Information about the Wix Media video.'),
    }),
  ])
);
export const BulkCreateProjectItemsRequest = z.object({
  options: z.object({
    items: z
      .array(
        z.intersection(
          z.object({
            projectId: z
              .string()
              .describe('Project ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            _id: z
              .string()
              .describe('Project item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            sortOrder: z
              .number()
              .describe(
                'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
              )
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Project item title.')
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Project item description.')
              .optional()
              .nullable(),
            type: z.enum(['UNDEFINED', 'IMAGE', 'VIDEO']).optional(),
            _createdDate: z
              .date()
              .describe('Date and time the project item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the project item was last updated.')
              .optional()
              .nullable(),
            link: z
              .object({
                text: z
                  .string()
                  .describe('Display text of the link.')
                  .optional()
                  .nullable(),
                url: z
                  .string()
                  .describe('Target URL of the link.')
                  .url()
                  .optional()
                  .nullable(),
                target: z
                  .string()
                  .describe(
                    "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                  )
                  .max(50)
                  .optional()
                  .nullable(),
              })
              .describe('Project item link.')
              .optional(),
          }),
          z.xor([
            z.object({
              image: z.never().optional(),
              video: z.never().optional(),
            }),
            z.object({
              video: z.never().optional(),
              image: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                })
                .describe('Information about the Wix Media image.'),
            }),
            z.object({
              image: z.never().optional(),
              video: z
                .object({
                  videoInfo: z
                    .string()
                    .describe('Information about the Wix Media video.')
                    .optional(),
                  durationInMillis: z
                    .number()
                    .int()
                    .describe(
                      'Manually defined Video duration in milliseconds.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Information about the Wix Media video.'),
            }),
          ])
        )
      )
      .min(1)
      .max(1000),
    returnFullEntity: z
      .boolean()
      .describe(
        'Whether to return the created project items.\n\nSet to `true` to return the project items in the response.\n\nDefault: `false`'
      )
      .optional()
      .nullable(),
  }),
});
export const BulkCreateProjectItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the created project item.\nIncluding its ID, index in the bulk request and whether it was successfully created.'
          )
          .optional(),
        item: z
          .intersection(
            z.object({
              projectId: z
                .string()
                .describe('Project ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              _id: z
                .string()
                .describe('Project item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              sortOrder: z
                .number()
                .describe(
                  'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Project item title.')
                .optional()
                .nullable(),
              description: z
                .string()
                .describe('Project item description.')
                .optional()
                .nullable(),
              type: z
                .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
                .describe('Project item data type.')
                .optional(),
              _createdDate: z
                .date()
                .describe('Date and time the project item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the project item was last updated.')
                .optional()
                .nullable(),
              link: z
                .object({
                  text: z
                    .string()
                    .describe('Display text of the link.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe('Target URL of the link.')
                    .url()
                    .optional()
                    .nullable(),
                  target: z
                    .string()
                    .describe(
                      "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe('Project item link.')
                .optional(),
            }),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                image: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                  })
                  .describe('Information about the Wix Media image.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z
                  .object({
                    videoInfo: z
                      .string()
                      .describe('Information about the Wix Media video.')
                      .optional(),
                    durationInMillis: z
                      .number()
                      .int()
                      .describe(
                        'Manually defined Video duration in milliseconds.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about the Wix Media video.'),
              }),
            ])
          )
          .describe(
            'Created project item. Only returned if `returnEntity` is set to `true` in the request.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Create Project Items.'
    )
    .optional(),
});
export const GetProjectItemRequest = z.object({
  itemId: z
    .string()
    .describe('Project item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetProjectItemResponse = z.intersection(
  z.object({
    projectId: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    _id: z
      .string()
      .describe('Project item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    sortOrder: z
      .number()
      .describe(
        'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project item title.').optional().nullable(),
    description: z
      .string()
      .describe('Project item description.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
      .describe('Project item data type.')
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the project item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project item was last updated.')
      .optional()
      .nullable(),
    link: z
      .object({
        text: z
          .string()
          .describe('Display text of the link.')
          .optional()
          .nullable(),
        url: z
          .string()
          .describe('Target URL of the link.')
          .url()
          .optional()
          .nullable(),
        target: z
          .string()
          .describe(
            "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Project item link.')
      .optional(),
  }),
  z.xor([
    z.object({ image: z.never().optional(), video: z.never().optional() }),
    z.object({
      video: z.never().optional(),
      image: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Information about the Wix Media image.'),
    }),
    z.object({
      image: z.never().optional(),
      video: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Information about the Wix Media video.'),
    }),
  ])
);
export const ListProjectItemsRequest = z.object({
  projectId: z
    .string()
    .describe('Project ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Maximum number of items to return in the results.')
        .optional(),
    })
    .optional(),
});
export const ListProjectItemsResponse = z.object({
  items: z
    .array(
      z.intersection(
        z.object({
          projectId: z
            .string()
            .describe('Project ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _id: z
            .string()
            .describe('Project item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          sortOrder: z
            .number()
            .describe(
              'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Project item title.')
            .optional()
            .nullable(),
          description: z
            .string()
            .describe('Project item description.')
            .optional()
            .nullable(),
          type: z
            .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
            .describe('Project item data type.')
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the project item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the project item was last updated.')
            .optional()
            .nullable(),
          link: z
            .object({
              text: z
                .string()
                .describe('Display text of the link.')
                .optional()
                .nullable(),
              url: z
                .string()
                .describe('Target URL of the link.')
                .url()
                .optional()
                .nullable(),
              target: z
                .string()
                .describe(
                  "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe('Project item link.')
            .optional(),
        }),
        z.xor([
          z.object({
            image: z.never().optional(),
            video: z.never().optional(),
          }),
          z.object({
            video: z.never().optional(),
            image: z
              .object({
                imageInfo: z
                  .string()
                  .describe('Information about the Wix Media image.')
                  .optional(),
                focalPoint: z
                  .object({
                    x: z
                      .number()
                      .describe('X-coordinate of the focal point.')
                      .optional(),
                    y: z
                      .number()
                      .describe('Y-coordinate of the focal point.')
                      .optional(),
                  })
                  .describe('Focal point of the image.')
                  .optional(),
              })
              .describe('Information about the Wix Media image.'),
          }),
          z.object({
            image: z.never().optional(),
            video: z
              .object({
                videoInfo: z
                  .string()
                  .describe('Information about the Wix Media video.')
                  .optional(),
                durationInMillis: z
                  .number()
                  .int()
                  .describe('Manually defined Video duration in milliseconds.')
                  .optional()
                  .nullable(),
              })
              .describe('Information about the Wix Media video.'),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadataV2: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const UpdateProjectItemRequest = z.object({
  _id: z
    .string()
    .describe('Project item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  item: z
    .intersection(
      z.object({
        projectId: z
          .string()
          .describe('Project ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _id: z
          .string()
          .describe('Project item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sortOrder: z
          .number()
          .describe(
            'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
          )
          .optional()
          .nullable(),
        title: z.string().describe('Project item title.').optional().nullable(),
        description: z
          .string()
          .describe('Project item description.')
          .optional()
          .nullable(),
        type: z.enum(['UNDEFINED', 'IMAGE', 'VIDEO']).optional(),
        _createdDate: z
          .date()
          .describe('Date and time the project item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the project item was last updated.')
          .optional()
          .nullable(),
        link: z
          .object({
            text: z
              .string()
              .describe('Display text of the link.')
              .optional()
              .nullable(),
            url: z
              .string()
              .describe('Target URL of the link.')
              .url()
              .optional()
              .nullable(),
            target: z
              .string()
              .describe(
                "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
              )
              .max(50)
              .optional()
              .nullable(),
          })
          .describe('Project item link.')
          .optional(),
      }),
      z.xor([
        z.object({ image: z.never().optional(), video: z.never().optional() }),
        z.object({
          video: z.never().optional(),
          image: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
            })
            .describe('Information about the Wix Media image.'),
        }),
        z.object({
          image: z.never().optional(),
          video: z
            .object({
              videoInfo: z
                .string()
                .describe('Information about the Wix Media video.')
                .optional(),
              durationInMillis: z
                .number()
                .int()
                .describe('Manually defined Video duration in milliseconds.')
                .optional()
                .nullable(),
            })
            .describe('Information about the Wix Media video.'),
        }),
      ])
    )
    .describe('The project item to update.'),
});
export const UpdateProjectItemResponse = z.intersection(
  z.object({
    projectId: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    _id: z
      .string()
      .describe('Project item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    sortOrder: z
      .number()
      .describe(
        'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project item title.').optional().nullable(),
    description: z
      .string()
      .describe('Project item description.')
      .optional()
      .nullable(),
    type: z
      .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
      .describe('Project item data type.')
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the project item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project item was last updated.')
      .optional()
      .nullable(),
    link: z
      .object({
        text: z
          .string()
          .describe('Display text of the link.')
          .optional()
          .nullable(),
        url: z
          .string()
          .describe('Target URL of the link.')
          .url()
          .optional()
          .nullable(),
        target: z
          .string()
          .describe(
            "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Project item link.')
      .optional(),
  }),
  z.xor([
    z.object({ image: z.never().optional(), video: z.never().optional() }),
    z.object({
      video: z.never().optional(),
      image: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Information about the Wix Media image.'),
    }),
    z.object({
      image: z.never().optional(),
      video: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Information about the Wix Media video.'),
    }),
  ])
);
export const BulkUpdateProjectItemsRequest = z.object({
  options: z
    .object({
      items: z
        .array(
          z.object({
            item: z
              .intersection(
                z.object({
                  projectId: z
                    .string()
                    .describe('Project ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  _id: z
                    .string()
                    .describe('Project item ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  sortOrder: z
                    .number()
                    .describe(
                      'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
                    )
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Project item title.')
                    .optional()
                    .nullable(),
                  description: z
                    .string()
                    .describe('Project item description.')
                    .optional()
                    .nullable(),
                  type: z.enum(['UNDEFINED', 'IMAGE', 'VIDEO']).optional(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the project item was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the project item was last updated.'
                    )
                    .optional()
                    .nullable(),
                  link: z
                    .object({
                      text: z
                        .string()
                        .describe('Display text of the link.')
                        .optional()
                        .nullable(),
                      url: z
                        .string()
                        .describe('Target URL of the link.')
                        .url()
                        .optional()
                        .nullable(),
                      target: z
                        .string()
                        .describe(
                          "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                        )
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe('Project item link.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z
                      .object({
                        imageInfo: z
                          .string()
                          .describe('Information about the Wix Media image.')
                          .optional(),
                        focalPoint: z
                          .object({
                            x: z
                              .number()
                              .describe('X-coordinate of the focal point.')
                              .optional(),
                            y: z
                              .number()
                              .describe('Y-coordinate of the focal point.')
                              .optional(),
                          })
                          .describe('Focal point of the image.')
                          .optional(),
                      })
                      .describe('Information about the Wix Media image.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z
                      .object({
                        videoInfo: z
                          .string()
                          .describe('Information about the Wix Media video.')
                          .optional(),
                        durationInMillis: z
                          .number()
                          .int()
                          .describe(
                            'Manually defined Video duration in milliseconds.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Information about the Wix Media video.'),
                  }),
                ])
              )
              .describe('Item to be updated.'),
          })
        )
        .max(100)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the updated project items.\n\nSet to `true` to return the project items in the response.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkUpdateProjectItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the updated project item.\nIncluding its ID, index in the bulk request and whether it was successfully updated.'
          )
          .optional(),
        item: z
          .intersection(
            z.object({
              projectId: z
                .string()
                .describe('Project ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              _id: z
                .string()
                .describe('Project item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              sortOrder: z
                .number()
                .describe(
                  'Index that determines which position a project is displayed in the project.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Project item title.')
                .optional()
                .nullable(),
              description: z
                .string()
                .describe('Project item description.')
                .optional()
                .nullable(),
              type: z
                .enum(['UNDEFINED', 'IMAGE', 'VIDEO'])
                .describe('Project item data type.')
                .optional(),
              _createdDate: z
                .date()
                .describe('Date and time the project item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the project item was last updated.')
                .optional()
                .nullable(),
              link: z
                .object({
                  text: z
                    .string()
                    .describe('Display text of the link.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe('Target URL of the link.')
                    .url()
                    .optional()
                    .nullable(),
                  target: z
                    .string()
                    .describe(
                      "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe('Project item link.')
                .optional(),
            }),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                image: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                  })
                  .describe('Information about the Wix Media image.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z
                  .object({
                    videoInfo: z
                      .string()
                      .describe('Information about the Wix Media video.')
                      .optional(),
                    durationInMillis: z
                      .number()
                      .int()
                      .describe(
                        'Manually defined Video duration in milliseconds.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Information about the Wix Media video.'),
              }),
            ])
          )
          .describe(
            'Updated project item. Only returned if `returnEntity` is set to `true` in the request.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Update Project Items.'
    )
    .optional(),
});
export const DeleteProjectItemRequest = z.object({
  itemId: z
    .string()
    .describe('ID of the project item to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteProjectItemResponse = z.object({
  itemId: z
    .string()
    .describe('ID of the deleted project item.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkDeleteProjectItemsRequest = z.object({
  options: z.object({ itemIds: z.array(z.string()).min(1) }),
});
export const BulkDeleteProjectItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the deleted project item.\nIncluding its ID, index in the bulk request and whether it was successfully deleted.'
          )
          .optional(),
        itemId: z
          .string()
          .describe('ID of the deleted project item.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Delete Project Items.'
    )
    .optional(),
});
export const DuplicateProjectItemsRequest = z.object({
  originProjectId: z
    .string()
    .describe('ID of the project containing the items to duplicate.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    targetProjectId: z
      .string()
      .describe('ID of the project where the duplicated items will be added.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const DuplicateProjectItemsResponse = z.object({
  projectId: z
    .string()
    .describe('Project ID where the duplicated items have been added.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const GenerateTokenForProjectItemsRequest = z.object({
  mediaIds: z.array(z.string()).min(1),
});
export const GenerateTokenForProjectItemsResponse = z.object({
  mediaTokens: z
    .array(
      z.object({
        mediaId: z.string().describe('Media id of project item').optional(),
        mediaToken: z
          .string()
          .describe('Generated media token for project item')
          .optional(),
      })
    )
    .optional(),
});
